import {
  lei,
  localDay,
  moneyBySite,
  ownerReport,
  rangeOf,
  siteLabel,
  type RangeKind,
  t,
  intlLocale,
} from '@procumi/shared';
import { useMemo, useState } from 'react';
import { Empty, Panel } from '../components/Panel';
import { ErrorBox, Loading } from '../components/StateBox';
import { useAsync } from '../hooks/useAsync';
import { api } from '../lib/api';
import type { Route } from '../routes';

const PERIOADE: { kind: RangeKind; label: string; name: string }[] = [
  { kind: 'month', label: t('Luna asta'), name: t('luna asta') },
  { kind: '90d', label: t('90 de zile'), name: t('ultimele 90 de zile') },
  { kind: 'year', label: t('Anul'), name: t('anul acesta') },
];

const pct = (x: number) => `${Math.round(x * 100)}%`;

/**
 * Raportul pentru conducere (patron, director, CFO): un singur ecran cu ce
 * contează pentru cine plătește, din aceleași date ca panoul (`/api/dashboard`):
 * comandat, economisit, alegerile peste cea mai mică ofertă, aprobările,
 * furnizorii de care depinde firma și concurența din cereri. Cifrele se
 * socotesc în `shared/ownerReport.ts`; același raport pleacă lunar pe mail.
 */
export function ReportPage({ go }: { go: (r: Route) => void }) {
  const sumar = useAsync(() => api.dashboard(), [], 'dashboard');
  const [kind, setKind] = useState<RangeKind>('month');
  const today = localDay(Date.now());
  const range = useMemo(() => rangeOf(kind, today), [kind, today]);
  const d = sumar.data;
  const r = useMemo(
    () => (d ? ownerReport({ stats: d.stats ?? [], orders: d.orders, toApprove: d.toApprove }, range) : null),
    [d, range],
  );
  const sites = useMemo(
    () => (d ? moneyBySite(d.orders, d.budgets, today).filter((s) => s.budget != null) : []),
    [d, today],
  );

  if (sumar.loading) return <Loading />;
  if (sumar.error || !d || !r) {
    return <ErrorBox error={sumar.error ?? t('Nu am putut încărca.')} onRetry={sumar.reload} />;
  }
  const perioada = PERIOADE.find((p) => p.kind === kind)!.name;
  const top = r.suppliers[0];

  return (
    <>
      <div className="head">
        <div>
          <h1>{t('Raport pentru conducere')}</h1>
          <p className="head-facts">
            {r.ordered.count
              ? t(
                  'În {0}: {1} comandat în {2} {3}{4}.',
                  perioada,
                  lei(r.ordered.value),
                  r.ordered.count,
                  r.ordered.count === 1 ? t('comandă') : t('comenzi'),
                  r.savings.realized > 0 ? t(', cu {0} sub a doua ofertă', lei(r.savings.realized)) : '',
                )
              : t('În {0} nu s-a emis încă nicio comandă.', perioada)}
          </p>
        </div>
        <div className="seg" role="radiogroup" aria-label={t('Perioada')}>
          {PERIOADE.map((p) => (
            <button
              key={p.kind}
              type="button"
              role="radio"
              aria-checked={kind === p.kind}
              onClick={() => setKind(p.kind)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rp-kpis">
        <div className="sheet rp-kpi">
          <span>{t('Comandat')}</span>
          <b>{lei(r.ordered.value)}</b>
          <small>
            {r.ordered.trend == null
              ? t('fără comenzi în perioada de dinainte')
              : t('{0}{1}% față de perioada de dinainte', r.ordered.trend > 0 ? '+' : '', r.ordered.trend)}
          </small>
        </div>
        <div className="sheet rp-kpi good">
          <span>{t('Economisit')}</span>
          <b>{lei(r.savings.realized)}</b>
          <small>
            {t(
              'față de a doua ofertă, la {0} din {1} {2}',
              r.savings.realizedCount,
              r.savings.decided,
              r.savings.decided === 1 ? t('cerere hotărâtă') : t('cereri hotărâte'),
            )}
          </small>
        </div>
        <div className={`sheet rp-kpi${r.savings.missed > 0 ? ' warn' : ''}`}>
          <span>{t('Rămas pe masă')}</span>
          <b>{lei(r.savings.missed)}</b>
          <small>{t('diferența până la cel mai bun preț posibil, la cererile hotărâte')}</small>
        </div>
        <button
          type="button"
          className={`sheet rp-kpi${r.approvals.count ? ' act' : ''}`}
          onClick={() => go({ view: 'dashboard' })}
        >
          <span>{t('Așteaptă aprobarea ta')}</span>
          <b>
            {r.approvals.count} {r.approvals.count === 1 ? t('comandă') : t('comenzi')}
          </b>
          <small>
            {r.approvals.count ? t('{0} · deschide panoul', lei(r.approvals.value)) : t('nimic de aprobat')}
          </small>
        </button>
      </div>

      <div className="an-grid">
        <Panel
          className="an-wide"
          title={t('Alese peste cea mai mică ofertă')}
          hint={t('poate cu motiv (termen, calitate, transport): merită întrebat')}
        >
          {r.pricier.length ? (
            <ul className="rp-list">
              {r.pricier.map((p) => (
                <li key={p.rfqId}>
                  <button type="button" className="rp-row" onClick={() => go({ view: 'rfq', id: p.rfqId })}>
                    <span>
                      <b>{p.title}</b>
                      <small>
                        {t('{0} · ales {1}, cea mai mică {2}', p.site, lei(p.chosen), lei(p.cheapest))}
                      </small>
                    </span>
                    <b className="rp-diff">+{lei(p.diff)}</b>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <Empty>
              {r.savings.decided
                ? t('Toate cererile hotărâte în perioadă au mers la cea mai mică ofertă completă.')
                : t('Apare după ce se alege furnizorul la o cerere cu cel puțin două oferte.')}
            </Empty>
          )}
        </Panel>

        <Panel title={t('Pe ce se duc banii')} hint={t('comandat în perioadă')}>
          {r.categories.length ? (
            <ul className="rp-bars">
              {r.categories.map((c) => (
                <li key={c.category ?? 'altele'}>
                  <span>
                    {c.category ?? t('Altele')} <small>{pct(c.share)}</small>
                  </span>
                  <b>{lei(c.lei)}</b>
                  <i style={{ width: pct(c.share) }} aria-hidden="true" />
                </li>
              ))}
            </ul>
          ) : (
            <Empty>{t('Apare după prima comandă din perioadă.')}</Empty>
          )}
        </Panel>

        <Panel title={t('Furnizorii')} hint={t('cât ia fiecare din comenzi')}>
          {top ? (
            <>
              <ul className="rp-bars">
                {r.suppliers.map((s) => (
                  <li key={s.name}>
                    <span>
                      {s.name} <small>{pct(s.share)}</small>
                    </span>
                    <b>{lei(s.value)}</b>
                    <i style={{ width: pct(s.share) }} aria-hidden="true" />
                  </li>
                ))}
              </ul>
              {r.topShare > 0.5 && r.suppliers.length > 1 ? (
                <p className="rp-note warn">
                  {t(
                    '{0} ia {1} din comenzi. Dacă nu poate livra, firma depinde de el: cere oferte și de la alții.',
                    top.name,
                    pct(r.topShare),
                  )}
                </p>
              ) : null}
            </>
          ) : (
            <Empty>{t('Apare după prima comandă din perioadă.')}</Empty>
          )}
        </Panel>

        <Panel title={t('Concurența')} hint={t('cererile trimise în perioadă')}>
          {r.competition.rfqs ? (
            <div className="rp-comp">
              <p>
                <b>
                  {r.competition.offersPerRfq!.toLocaleString(intlLocale(), { maximumFractionDigits: 1 })}
                </b>
                <span>{t('oferte primite în medie pe cerere')}</span>
              </p>
              <p className={r.competition.single ? 'warn' : ''}>
                <b>{r.competition.single}</b>
                <span>
                  {t(
                    '{0} din {1} cu cel mult o ofertă: fără comparație, prețul nu e verificat',
                    r.competition.single === 1 ? t('cerere') : t('cereri'),
                    r.competition.rfqs,
                  )}
                </span>
              </p>
            </div>
          ) : (
            <Empty>{t('Apare după prima cerere trimisă în perioadă.')}</Empty>
          )}
        </Panel>

        {sites.length ? (
          <Panel
            className="an-wide"
            title={t('Bugetul pe șantiere')}
            hint={t('comandat de la început, față de buget')}
          >
            <ul className="rp-bars">
              {sites.map((s) => {
                const used = s.bought + s.committed;
                const share = s.budget ? used / s.budget : 0;
                return (
                  <li key={s.site.id} className={share > 1 ? 'over' : share > 0.9 ? 'near' : ''}>
                    <span>
                      {siteLabel(s.site)} <small>{t('{0} din {1}', pct(share), lei(s.budget!))}</small>
                    </span>
                    <b>{lei(used)}</b>
                    <i style={{ width: pct(Math.min(share, 1)) }} aria-hidden="true" />
                  </li>
                );
              })}
            </ul>
          </Panel>
        ) : null}
      </div>
    </>
  );
}
